import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { RecurrenceScope } from '@lira/shared';
import { Modal } from '@/components/ui';
import { cn } from '@/lib/cn';

interface RecurrenceScopeDialogProps {
  open: boolean;
  title: string;
  /** Rótulo do botão de confirmação (ex.: "Salvar", "Excluir"). */
  confirmLabel: string;
  /** true => botão de confirmação vermelho (exclusão). */
  danger?: boolean;
  loading?: boolean;
  onConfirm: (scope: RecurrenceScope) => void;
  onCancel: () => void;
}

const SCOPES: { value: RecurrenceScope; label: string; hint: string }[] = [
  { value: 'one', label: 'Somente esta', hint: 'Afeta apenas este lançamento.' },
  { value: 'future', label: 'Esta e as próximas', hint: 'Sobrescreve ajustes individuais futuros.' },
  { value: 'all', label: 'Todas', hint: 'Aplica a toda a série, passada e futura.' },
];

/** Escolha do escopo (one|future|all) ao editar/excluir um lançamento recorrente. */
export function RecurrenceScopeDialog({
  open,
  title,
  confirmLabel,
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}: RecurrenceScopeDialogProps) {
  const [scope, setScope] = useState<RecurrenceScope>('one');

  useEffect(() => {
    if (open) setScope('one');
  }, [open]);

  return (
    <Modal open={open} onClose={onCancel} title={title} maxWidth={420}>
      <p className="text-sm text-muted">Este lançamento faz parte de uma série recorrente.</p>

      <div className="mt-4 space-y-2" role="radiogroup" aria-label="Escopo">
        {SCOPES.map((s) => {
          const active = s.value === scope;
          return (
            <button
              key={s.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setScope(s.value)}
              className={cn(
                'flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors',
                active ? 'border-primary bg-primary-weak' : 'border-border hover:border-muted',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 grid size-[18px] shrink-0 place-items-center rounded-full border-2 transition-colors',
                  active ? 'border-primary' : 'border-border',
                )}
              >
                {active && <span className="size-2 rounded-full bg-primary" />}
              </span>
              <span className="min-w-0">
                <span className="block text-[15px] font-medium text-ink">{s.label}</span>
                <span className="block text-[13px] text-muted">{s.hint}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-10 rounded-full px-5 text-sm font-semibold uppercase tracking-wide text-muted transition-colors hover:bg-canvas hover:text-ink"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => onConfirm(scope)}
          disabled={loading}
          className={cn(
            'flex h-10 items-center gap-2 rounded-full px-5 text-sm font-semibold uppercase tracking-wide text-white transition-opacity disabled:opacity-60',
            danger ? 'bg-expense hover:opacity-90' : 'bg-primary hover:bg-primary-hover',
          )}
        >
          {loading && <Loader2 className="size-4 animate-spin" />}
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
